import { useCallback, useEffect, useState } from "react";
import { useEffectEvent } from "./useEffectEvent.js";

// TODO: Get rid of this once `TableLoadMoreItem` is available in React Aria. See https://github.com/adobe/react-spectrum/pull/8431/files#diff-1acf6587178822e22a57ab7fbc06edb252351a1837b53a58e656b7e4f9405528

/**
 * Hook for implementing infinite scroll with IntersectionObserver.
 *
 * @param props.entities - The current list of entities
 * @param props.unfilteredEntityCount - Optional count of entities before filtering (to trigger loading even when all filtered)
 * @param props.onLoadMore - Callback to load more items
 * @returns An object with canLoadMore flag and loadMoreSentinelRef to attach to the sentinel element
 */
export function useInfiniteScroll(props: {
  entities: readonly unknown[] | undefined;
  unfilteredEntityCount?: number;
  onLoadMore?: () => void;
}) {
  const canLoadMore = props.onLoadMore !== undefined;
  const handleLoadMore = useEffectEvent(() => {
    props.onLoadMore?.();
  });

  // Use a ref callback and state to track the sentinel element, because the `Table` component may not render it after the first cycle
  // TODO: Use a ref callback with cleanup function once we upgrade to React 19
  const [loadMoreSentinel, setLoadMoreSentinel] =
    useState<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useCallback((element: HTMLDivElement | null) => {
    setLoadMoreSentinel(element);
  }, []);

  // Once we have a sentinel element, check whether it's visible to determine whether we should load more.
  // If the number of entities changes, we may need to load (even) more.
  const canLoadMoreAtEntityCount = canLoadMore
    ? (props.unfilteredEntityCount ?? props.entities?.length ?? 0)
    : undefined;
  useEffect(() => {
    if (canLoadMoreAtEntityCount === undefined) {
      return;
    }

    // Check whether the element exists and lives in a window
    const window = loadMoreSentinel?.ownerDocument.defaultView;
    if (!window) {
      return;
    }

    const observer = new window.IntersectionObserver((entries) => {
      if (entries?.at(-1)?.isIntersecting) {
        handleLoadMore();
      }
    });

    // This will immediately (but asynchronously) check whether the sentinel is visible and then when the visibility changes
    observer.observe(loadMoreSentinel);

    return () => observer.disconnect();
  }, [canLoadMoreAtEntityCount, handleLoadMore, loadMoreSentinel]);

  return { canLoadMore, loadMoreSentinelRef };
}
